import { useCallback, useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { KioSelect } from "../common/KioSelect";
import { usePlausibleSummary, usePlausibleDetailed, usePlausibleList } from "@/hooks/useFetchStatistics";
import Styles from "@/assets/styles/Styles";
import KioTitle from "../common/KioTitle";
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, Title, Tooltip, Legend, PointElement, BarElement, Filler, type TooltipItem } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Button, LinearProgress, Pagination, Typography, Tab, Tabs, TableContainer, Table, Paper, TableHead, TableCell, TableRow, TableBody } from "@mui/material";
import { Close, OpenInNew } from "@mui/icons-material";
import Loader from "@/framework/Loader";
import { useParams, useSearchParams } from "react-router-dom";
import { buildDateRangeOptions, buildDetailsFormatters, pickDetailsLabel, pickMetrics, METRICS, type DateRangeOption, type StatsTab } from "@/utils/statistics";
import type { UiSchema, WidgetProps } from "@rjsf/utils";
import { getValueFromUiSchema } from "@/utils/schema";
import { PaginatorContainer } from "@/components/DataList/DataList";
import type { PlausibleListRow, PlausibleMetricId } from "@/declarations/models/Plausible";
ChartJS.register(CategoryScale, LinearScale, LineElement, Title, Tooltip, Legend, PointElement, BarElement, Filler);

const StyledStatisticsWidgetContainer = styled("div")`
    padding: 16px;
`;
const StyledHeader = styled("div")`
    display: flex;
    flex-direction: row;
    gap: 20px;
    align-items: flex-end;
    margin-bottom: 16px;
    button {
        height: 100%;
    }
    > div:first-of-type {
        margin-right: auto;
    }
    >:last-child {
        max-width: 200px;
    }
`;

const StyledStatisticsSummary = styled("div")<{$noOfItems?: number}>`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 16px;
    max-width: ${({ $noOfItems }) => {
        const n = $noOfItems ?? 6;
        return `${n * 250 + (n - 1) * 16}px`;
    }};
`;

const StyledStatisticsSummaryItem = styled(Button)`
    position: relative;
    width: 100%;
    max-width: 250px;
    border: 1px solid #CCC;
    background-color: transparent;
    border-radius: 4px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    text-transform: none;
    text-align: left;
    color: unset;

    &.active {
        font-weight: 700;
        color: ${Styles.Colors.THEME_PRIMARY};
        background-color: ${Styles.Colors.THEME_PRIMARY}08;
    }
    &.active::after {
        content: "";
        position: absolute;
        left: 50%;
        bottom: -8px;
        transform: translateX(-50%);
        width: 50px;
        height: 4px;
        background: ${Styles.Colors.THEME_PRIMARY};
    }
`;

const StyledLineChartContainer = styled("div")`
    width: 100%;
    max-height: 100%;
    height: 300px;
    margin: 16px 0;
    canvas {
        height: 100% !important;
        width: 100% !important;
        margin: 0 !important;
    }
`;
const StyledListContainer = styled(StyledLineChartContainer)`
    height: 100%;
    > div {
        margin: 16px 0;
    }
`;

const StyledTableRow = styled(TableRow)`
    td {
        height: 100%;
        padding: 0;

        &.select {
        width: 50%;
            button {
                padding: 16px 16px;
                justify-content: flex-start;
                text-align: left;
                border-left: 4px solid transparent;
            }
        }
        &.value {
            text-align: center;
            white-space: nowrap;
        }
        &.progress {
            padding: 8px 12px;
            min-width: 100px;
            max-width: 300px;
        }
        &.action {
            min-width: 48px;
            text-align: center;
        }
        button {
            padding: 16px 16px;
            border-radius: 0;
            text-transform: none;
            width: 100%;
            white-space: normal;
            overflow-wrap: anywhere; 
            .listButtonSubTitle {
                color: ${Styles.Colors.GREY_600};
            }
        }
    }
    
    &.active { 
        background-color: ${Styles.Colors.THEME_PRIMARY}08;
        .select button {
            border-left: 4px solid ${Styles.Colors.THEME_PRIMARY};
        }
    }
`;

const StyledOpenPageButton = styled(Button)`
    min-width: 0;
    max-width: 48px;
    padding: 16px 20px 16px 16px;
    color: ${Styles.Colors.THEME_PRIMARY};
`;

const PAGE_SIZE = 10;

export const StatisticsWidget: FC<WidgetProps> = ({ uiSchema }) => {
    const { documentId } = useParams<{
        documentId: string;
    }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t, i18n } = useTranslation("common");
    const metrics = useMemo(() => METRICS(t), [t]);
    const detailsFormatters = useMemo(() => buildDetailsFormatters([i18n.language, "nb"]), [i18n.language]);
    const plausiblePropertyFromSchema = getValueFromUiSchema("plausibleProperty", uiSchema);
    const hideQrCodes  = getValueFromUiSchema<boolean>("hideQrCodes",  uiSchema) ?? false;
    const hideOpenPage = getValueFromUiSchema<boolean>("hideOpenPage", uiSchema) ?? false;
    const hidePagePath = getValueFromUiSchema<boolean>("hidePagePath", uiSchema) ?? false;
    const { options: dateRangeOptions, defaultOption: defaultRange } = useMemo(() => buildDateRangeOptions(t), [t]);
    const findRange = (v: string | null): DateRangeOption => dateRangeOptions.find(o => o.value === v) ?? defaultRange;
    const tabId    = (v: string | number) => `tab-${v}`;
    const panelId  = (v: string | number) => `tabpanel-${v}`;

    const suitableDimensions = (dateRangeId: string) => {
        switch (dateRangeId) {
            case "today":
            case "yesterday":
            case "24h":
                return "time:hour";
            default:
                return "time:day";
        }
    };

    const [selectedMetric, setSelectedMetric] = useState<PlausibleMetricId>("visitors");
    const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption>(() => findRange(searchParams.get("dateRange")));
    const [selectedPage, setSelectedPage] = useState<PlausibleListRow | null>(null);
    const [selectedQr, setSelectedQr] = useState<PlausibleListRow | null>(null);
    const [selectedLanguage, setSelectedLanguage] = useState<PlausibleListRow | null>(null);
    const [selectedDimension, setSelectedDimension] = useState<string | null>(suitableDimensions(selectedDateRange.value));
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [currentQrPage, setCurrentQrPage] = useState<number>(0);
    const [currentLangPage, setCurrentLangPage] = useState<number>(0);
    const [currentTab, setCurrentTab] = useState<string | number>("pages");

    const requestedMetrics = useMemo(() => pickMetrics(selectedDateRange.value, !!selectedPage, !!selectedQr), [selectedDateRange.value, selectedPage, selectedQr]);

    const common = {
        dateRange: selectedDateRange.plausibleRange,
        dimensions: selectedDimension,
        selectedPage: selectedPage?.dimensions?.[0] ?? null,
        selectedLanguage: selectedLanguage?.dimensions?.[0] ?? null,
        selectedQr: selectedQr?.dimensions?.[0] ?? null,
        documentId: documentId ?? null,
        plausibleProperty: plausiblePropertyFromSchema ?? null,
        pageUrl: uiSchema?.["ui:plausibleUrl"],
    };

    const hasFilter = selectedPage !== null || selectedLanguage !== null || selectedQr !== null;

    const totalSummary = usePlausibleSummary({ ...common, selectedPage: null, selectedLanguage: null, metrics: ["visitors"], enabled: hasFilter });
    const summary = usePlausibleSummary({ ...common, metrics: requestedMetrics, enabled: true });
    const detailed = usePlausibleDetailed({ ...common, selectedMetric: [selectedMetric] });
    const pages = usePlausibleList("pages", { ...common, offset: currentPage * PAGE_SIZE, filters: hideQrCodes ? [] : undefined });
    const qrCodes = usePlausibleList("qrcodes", { ...common, offset: currentQrPage * PAGE_SIZE, enabled: !hideQrCodes });
    const languages = usePlausibleList("languages", { ...common, offset: currentLangPage * PAGE_SIZE });

    const statsSummary = summary?.data;
    const statsDetailed = detailed.data;
    const statsPages = pages.data;
    const statsQRCodes = qrCodes.data;
    const statsLanguages = languages.data;
    const statsSummaryLoading = summary?.loading;
    const statsDetailedLoading = detailed.loading;
    const statsPagesLoading = pages.loading;
    const statsQRCodesLoading = qrCodes.loading;
    const statsLanguagesLoading = languages.loading;
    const totalVisitors = hasFilter ? Number(totalSummary?.data?.metrics?.[0] ?? 0) : Number(statsSummary?.metrics?.[0] ?? 0);

    const summaryItems = useMemo(() => requestedMetrics.map((id, i) => {
        const def = metrics[id];
        const raw = statsSummary?.metrics?.[i];
        return {
            id,
            label: def.label,
            value: raw == null ? null : (def.format ? def.format(raw) : String(raw)),
        };
    }), [requestedMetrics, statsSummary, t]);

    const activeMetricId = requestedMetrics.includes(selectedMetric) ? selectedMetric : requestedMetrics[0];
    const activeMetric = metrics[activeMetricId];
    const formatValue = useCallback((v: unknown) => activeMetric.format ? activeMetric.format(v) : String(v), [activeMetric]);
    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 0
            }
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: {
                    autoSkip: true,
                    autoSkipPadding: 12,
                    maxRotation: 0,
                    minRotation: 0,
                    color: Styles.Colors.THEME_PRIMARY,
                },
            },
            y: {
                min: 0,
                ticks: {
                    color: Styles.Colors.THEME_PRIMARY,
                    precision: 0,
                    callback: (value: number | string) => formatValue(value),
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: (ctx: TooltipItem<'line'>) => `${ctx.dataset.label ?? ''}: ${formatValue(ctx.parsed.y)}`,
                },
            },
        },
        interaction: { mode: 'index', intersect: false } as const,
        animation: {
            duration: 500,
            easing: "easeOutQuart"
        } as const
    }), [selectedDateRange, formatValue, activeMetric]);

    const detailedStatsChartData = useMemo(() => ({
        labels: statsDetailed?.map(column =>
            pickDetailsLabel(selectedDateRange.value, column.label, detailsFormatters),
        ),
        datasets: [{
            label: t(`stats.metrics.${selectedMetric}`) as string,
            data: statsDetailed?.map(column => Number(column.metric)) ?? [],
            borderWidth: 2,
            borderColor: Styles.Colors.THEME_PRIMARY,
            backgroundColor: `${Styles.Colors.THEME_PRIMARY}33`,
            fill: true,
            tension: 0.2,
            pointRadius: 0,
            pointHoverRadius: 4,
        }]
    }), [statsDetailed, selectedDateRange, detailsFormatters, t, selectedMetric]);

    const handlePageChange = (page: number, pageSize: number, totalNoOfPages: number, type: "pages" | "qrcodes" | "languages") => {
        const lastPageIndex = totalNoOfPages > 0 ? Math.ceil(totalNoOfPages / pageSize) - 1 : 0;
        const newCurrentPage = page >= lastPageIndex ? lastPageIndex : page;
        if (type === "pages") {
            setCurrentPage(newCurrentPage);
        } else if (type === "qrcodes") {
            setCurrentQrPage(newCurrentPage);
        } else if (type === "languages") {
            setCurrentLangPage(newCurrentPage);
        }
    };


    const listsTabs: StatsTab[] = useMemo(() => {
        return [
            {
                value: "pages",
                title: t("stats.usedPages"),
                notFoundText: t("stats.noPagesFound"),
                loadingText: t("stats.loadingPages"),
                loading: statsPagesLoading,
                resultItem: statsPages,
                hideOpenPage: hideOpenPage,
                hidePagePath: hidePagePath,
                selectedItem: selectedPage,
                onSelectItem: (page: PlausibleListRow) => {
                    setSelectedMetric("visitors");
                    setSelectedPage(prev => prev?.dimensions?.[0] === page.dimensions[0] ? null : page)
                },
                onHandlePageChange: (_: React.ChangeEvent<unknown>, newPage: number) => handlePageChange(newPage, PAGE_SIZE, statsPages?.meta?.total_rows ?? 0, "pages"),
                currentPage: currentPage,
                pageCount: statsPages?.meta?.total_rows ?? 0,
                hidden: false
            }, {
                value: "qrcodes",
                title: t("stats.usedQrCodes"),
                notFoundText: t("stats.noQrCodesFound"),
                loadingText: t("stats.loadingQrCodes"),
                loading: statsQRCodesLoading,
                resultItem: statsQRCodes,
                hideOpenPage: hideOpenPage,
                hidePagePath: hidePagePath,
                selectedItem: selectedQr,
                onSelectItem: (qr: PlausibleListRow) => {
                    setSelectedMetric("visitors");
                    setSelectedQr(prev => prev?.dimensions?.[0] === qr.dimensions[0] ? null : qr)
                },
                onHandlePageChange: (_: React.ChangeEvent<unknown>, newPage: number) => handlePageChange(newPage, PAGE_SIZE, statsQRCodes?.meta?.total_rows ?? 0, "qrcodes"),
                currentPage: currentQrPage,
                pageCount: statsQRCodes?.meta?.total_rows ?? 0,
                hidden: hideQrCodes
            }, {
                value: "languages",
                title: t("stats.usedLanguages"),
                notFoundText: t("stats.noLanguagesFound"),
                loadingText: t("stats.loadingLanguages"),
                loading: statsLanguagesLoading,
                resultItem: statsLanguages,
                hideOpenPage: true,
                hidePagePath: false,
                selectedItem: selectedLanguage,
                onSelectItem: (lang: PlausibleListRow) => {
                    setSelectedMetric("visitors");
                    setSelectedLanguage(prev => prev?.dimensions?.[0] === lang.dimensions[0] ? null : lang)
                },
                onHandlePageChange: (_: React.ChangeEvent<unknown>, newPage: number) => handlePageChange(newPage, PAGE_SIZE, statsLanguages?.meta?.total_rows ?? 0, "languages"),
                currentPage: currentLangPage,
                pageCount: statsLanguages?.meta?.total_rows ?? 0,
                hidden: false
            }
        ];
    }, [statsPages, statsQRCodes, statsLanguages, statsPagesLoading, statsQRCodesLoading, statsLanguagesLoading, selectedPage, selectedQr, selectedLanguage, currentPage, currentQrPage, currentLangPage, hideQrCodes, hideOpenPage, hidePagePath, t]);

    const visibleTabs = useMemo(() => listsTabs.filter(tab => !tab.hidden), [listsTabs]);

    return (
        <StyledStatisticsWidgetContainer id="statisticsWidget">
            <StyledHeader>
                <KioTitle title={t("stats.title")} level={0} />
                {selectedPage &&
                    <Button
                        variant="outlined"
                        onClick={() => { setSelectedPage(null); }}
                        type="button"
                    >
                        {selectedPage?.displayTitle ?? selectedPage?.dimensions?.[0]}<Close />
                    </Button>
                }
                {selectedQr &&
                    <Button
                        variant="outlined"
                        onClick={() => { setSelectedQr(null); }}
                        type="button"
                    >
                        {selectedQr?.displayTitle ?? selectedQr?.dimensions?.[0]}<Close />
                    </Button>
                }
                {selectedLanguage &&
                    <Button
                        variant="outlined"
                        onClick={() => { setSelectedLanguage(null); }}
                        type="button"
                    >
                        {selectedLanguage?.displayTitle ?? selectedLanguage?.dimensions?.[0]}<Close />
                    </Button>
                }
                <KioSelect
                    value={selectedDateRange.value}
                    onChange={(newValue) => {
                        const next = findRange(newValue);
                        setSelectedDateRange(next);
                        setSelectedDimension(suitableDimensions(next.value));
                        setSearchParams(params => { params.set("dateRange", next.value); return params; });
                        setCurrentPage(0);
                        setSelectedPage(null);
                    }}
                    isHeaderVariant={true}
                    options={dateRangeOptions}
                    displayEmpty
                />
            </StyledHeader>
            <div>
                <StyledStatisticsSummary $noOfItems={summaryItems.length} aria-busy={statsSummaryLoading}>
                    {summaryItems.map((item) => (
                        <StyledStatisticsSummaryItem
                            key={item.id}
                            type="button"
                            id={`${item.id}Button`}
                            onClick={() => setSelectedMetric(item.id)}
                            className={selectedMetric === item.id ? "active" : ""}
                            aria-pressed={selectedMetric === item.id}
                        >
                            <Typography component="span" fontSize={"18px"} fontWeight={"bold"}>{statsSummaryLoading ? "–" : (item.value ?? "N/A")}</Typography>{item.label}
                        </StyledStatisticsSummaryItem>
                    ))}
                </StyledStatisticsSummary>
                <StyledLineChartContainer>
                    {statsDetailedLoading ?
                        <Loader loadingText={t("stats.loadingStats")} size={24} />
                        :
                        <Line
                            aria-label={t("stats.chartForMetric", { metric: activeMetric.label })}
                            options={options}
                            data={detailedStatsChartData}
                        />
                    }
                </StyledLineChartContainer>
                <StyledListContainer>
                    <Tabs
                        value={currentTab}
                        indicatorColor="secondary"
                        textColor="primary"
                        onChange={(_, value) => setCurrentTab(value)}
                        variant="scrollable"
                        aria-label={t("stats.tabs")}
                    >
                        {visibleTabs.map((tab) => (
                            <Tab
                                id={tabId(tab.value)} 
                                aria-controls={panelId(tab.value)}
                                key={tab.value}
                                value={tab.value}
                                label={tab.title}
                            />
                        ))}
                    </Tabs>
                    {visibleTabs.map((tab) => (
                        <div
                            key={`tab-content-${tab.value}`}
                            id={panelId(tab.value)}
                            role="tabpanel"
                            aria-labelledby={tabId(tab.value)}
                            hidden={tab.value !== currentTab}
                        >   
                            <TabBody
                                tab={tab}
                                totalVisitors={totalVisitors}
                                uiSchema={uiSchema}
                            />
                            <PaginatorContainer style={{ marginTop: "auto" }}>
                                <Pagination
                                    page={tab.currentPage + 1}
                                    count={Math.ceil(tab.pageCount / PAGE_SIZE) || 0}
                                    onChange={(_: React.ChangeEvent<unknown>, newPage: number) => tab.onHandlePageChange(_, newPage - 1)}
                                    showFirstButton={true}
                                    showLastButton={true}
                                    hideNextButton={false}
                                    hidePrevButton={false}
                                    size="large"
                                />
                            </PaginatorContainer>
                        </div>
                    ))}
                </StyledListContainer>
            </div>
        </StyledStatisticsWidgetContainer>
    );
};


type TabBodyProps = {
    tab: StatsTab;
    totalVisitors: number;
    uiSchema?: UiSchema;
};

const TabBody: FC<TabBodyProps> = ({ tab, totalVisitors, uiSchema }) => {
    const { t } = useTranslation("common");
    if (tab.loading && !tab.resultItem?.results?.length) {
        return <Loader loadingText={tab.loadingText} size={24} />;
    }
    if (!tab.resultItem?.results?.length) {
        return <p>{tab.notFoundText}</p>;
    }
    return (
        <TableContainer component={Paper}>
            <Table sx={{ minWidth: 400 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ paddingLeft: "20px" }}>{t(`stats.${tab.value}`)}</TableCell>
                        <TableCell align="center">{t("stats.visitors")}</TableCell>
                        <TableCell>{t("stats.shareOfVisitors")}</TableCell>
                        {!tab.hideOpenPage && uiSchema?.["ui:plausibleUrl"] && (
                            <TableCell />
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tab?.resultItem?.results?.map((result: PlausibleListRow) => {
                        const visitors = Number(result.metrics[0]);
                        const pct = totalVisitors > 0 ? (visitors / totalVisitors) * 100 : 0;
                        const isActive = tab?.selectedItem?.dimensions?.[0] === result.dimensions[0];
                        return (
                            <StyledTableRow key={result.dimensions[0]} className={isActive ? 'active' : ''}>
                                <TableCell className="select">
                                    <Button
                                        type="button"
                                        onClick={() => tab.onSelectItem(result)}
                                        aria-pressed={isActive}
                                        className="listButton"
                                    >
                                        {result.displayTitle
                                            ? <span><Typography component="span" fontSize={"16px"} fontWeight={"bold"} className="listButtonMainTitle">{result.displayTitle}</Typography>{!tab.hidePagePath && <span className="listButtonSubTitle">{` (${result.dimensions[0]})`}</span>}</span>
                                            : <Typography component="span" fontSize={"16px"} fontWeight={"bold"} className="listButtonMainTitle">{result.dimensions[0]}</Typography>}
                                    </Button>
                                </TableCell>
                                <TableCell className="value">{visitors}</TableCell>
                                <TableCell className="progress" >
                                    {pct.toFixed(2)}% <LinearProgress value={Math.min(100, Math.max(0, pct))} variant="determinate" color="secondary" />
                                </TableCell>
                                {!tab.hideOpenPage && uiSchema?.["ui:plausibleUrl"] && (
                                    <TableCell className="action">
                                        <StyledOpenPageButton
                                            type="button"
                                            id={`schema-menu-button-${result.dimensions[0]}`}
                                            color="secondary"
                                            onClick={() => window.open(`https://${uiSchema["ui:plausibleUrl"]}${result.dimensions[0]}`, '_blank', 'noopener,noreferrer')}
                                            aria-label={t("stats.openPageInNewTab", { page: result.dimensions[0] })}
                                        >
                                            <OpenInNew />
                                        </StyledOpenPageButton>
                                    </TableCell>
                                )}
                            </StyledTableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default StatisticsWidget;
